import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Box, Drawer, IconButton, useMediaQuery } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { PHONE_SCREEN } from "../../utils/layout";
import useIndices from "../../hooks/useIndices";
import { useTranslation } from "react-i18next";
import logo from "../../assets/bluebell-lockup.png";
import Sidebar from "./Sidebar";
import FieldsPage from "./FieldsPage";
import OwnersPage from "./OwnersPage";
import ChartsPage from "./ChartsPage";
import AlertsPage from "./AlertsPage";
import "./Dashboard.css";

const Dashboard = () => {
  const navigate = useNavigate();
  const phone = useMediaQuery(PHONE_SCREEN);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();
  // Index names and units for the Charts page and the field charts, even
  // when the dashboard is opened directly.
  useIndices();

  // The user can not reload the dashboard page
  useEffect(() => {
    const [navigationEntry] = performance.getEntriesByType("navigation");
    const isReload = navigationEntry && navigationEntry.type === "reload";

    if (isReload) {
      // This item is to save page reload state; performance.getEntriesByType() is not consistant
      const hasRedirected = sessionStorage.getItem(
        "has_redirected_after_reload",
      );

      if (!hasRedirected) {
        sessionStorage.setItem("has_redirected_after_reload", "true");
        navigate("/", { replace: true });
      }
    }

    // Warn the user if they try to refresh/close the tab
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      sessionStorage.removeItem("has_redirected_after_reload");
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [navigate]);

  return (
    <div className="dashboard-container">
      {phone ? (
        // Phones: a top bar; the sidebar slides in.
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              height: 56,
              flexShrink: 0,
              bgcolor: "#fff",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <IconButton
              onClick={() => setMenuOpen(true)}
              aria-label={t("dashboard.menu")}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src={logo}
              alt="Bluebell"
              sx={{ height: 24 }}
            />
          </Box>
          <Drawer open={menuOpen} onClose={() => setMenuOpen(false)}>
            <Sidebar onClose={() => setMenuOpen(false)} />
          </Drawer>
        </>
      ) : (
        <Sidebar />
      )}
      <div className="dashboard-content">
        <Routes>
          <Route path="/" element={<Navigate to="fields" replace />} />
          <Route path="fields" element={<FieldsPage />} />
          <Route path="owners" element={<OwnersPage />} />
          <Route path="charts" element={<ChartsPage />} />
          <Route path="alerts" element={<AlertsPage />} />
        </Routes>
      </div>
    </div>
  );
};

export default Dashboard;
